import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { DataGrid } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import Divider from '@mui/material/Divider';
import KeyIcon from '@mui/icons-material/Key';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import { useGetRolesQuery } from 'services/roleApi';
import { useNotification } from 'hook/useNotification';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import MDTypography from 'components/MDTypography';

/**
 * Component for displaying and managing the details of an API key. Allows viewing,
 * creating, and updating API keys.
 *
 * @component
 *
 * @example
 * <ApiKeyDetails apiKey={apiKey} onSubmit={handleSubmit} />
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.apiKey - The API key object to be displayed and edited.
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * @param {Function} props.onFieldChange - The function to call when the fields value change.
 *
 * @returns {React.Component} The API Key Details component.
 */
function ApiKeyDetails({ apiKey, onSubmit, onFieldChange }) {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const { data: roles = [], isLoading: roleLoading, isError: roleError } = useGetRolesQuery();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      id: '',
      key: '',
      owner: '',
      validityPeriod: '',
      status: 'ACTIVATED',
      description: ''
    }
  });

  watch();

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(isDirty);
    }
  }, [isDirty, onFieldChange]);

  const resetApiKey = useCallback(() => {
    if (apiKey?.owner) {
      reset(apiKey);
    } else {
      reset({
        id: '',
        key: '',
        owner: '',
        validityPeriod: '',
        status: 'ACTIVATED',
        description: ''
      });
    }
    setRowSelectionModel(apiKey?.roles.map((role) => role.id) ?? []);
  }, [apiKey, reset]);

  useEffect(() => {
    resetApiKey();
  }, [apiKey, resetApiKey]);

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  const handleReset = () => {
    resetApiKey();
  };

  const handleRoleSelectionChange = (selectionModel) => {
    const selectedRoles = selectionModel.map((id) => roles.find((role) => role.id === id));
    reset((prevValues) => ({
      ...prevValues,
      roles: selectedRoles
    }));
    setValue('roles', selectedRoles);
  };

  const handleRowSelectionModelChange = (newRowSelectionModel) => {
    const selectedRoles = newRowSelectionModel.map((id) => roles.find((role) => role.id === id));
    setRowSelectionModel(newRowSelectionModel);
    setValue('roles', selectedRoles);
  };

  const isCreating = !apiKey?.id;

  if (roleError) {
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.apikeys.role.fetch.error' })
      });
    } catch {
      // Ignore exception
    }
  }

  if (roleLoading) {
    return <IndeterminateLoading />;
  }

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      display="flex"
      direction="column"
      sx={{ height: '100%' }}
    >
      <Stack display="flex" direction="row">
        <KeyIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
        <MDTypography variant="h5">
          {msg({
            id: isCreating
              ? 'page.apikeys.details.creation.title'
              : 'page.apikeys.details.modification.title'
          })}
        </MDTypography>
      </Stack>
      <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />
      <Stack
        spacing={1}
        display="flex"
        direction="column"
        flex={1}
        overflow="auto"
        mb={2}
        sx={{ scrollbarWidth: 'thin' }}
      >
        {!isCreating && (
          <>
            <Controller
              name="id"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  label={msg({ id: 'field.id.label' })}
                  variant="filled"
                  fullWidth
                  required
                  size="small"
                  InputProps={{
                    readOnly: true
                  }}
                />
              )}
            />
            <Controller
              name="displayableKey"
              control={control}
              render={({ field }) => (
                <MDInput
                  required
                  {...field}
                  label={msg({ id: 'field.apiKey.label' })}
                  variant="filled"
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    value: apiKey?.displayableKey
                  }}
                />
              )}
            />
          </>
        )}
        <Controller
          name="owner"
          control={control}
          rules={{ required: msg({ id: 'field.owner.required' }) }}
          render={({ field }) => (
            <MDInput
              required
              {...field}
              label={msg({ id: 'field.owner.label' })}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors.owner}
              helperText={errors.owner ? errors.owner.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('owner')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="validityPeriod"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.validityPeriod.label' })}
              variant="filled"
              fullWidth
              size="small"
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('validityPeriod')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <FormControl variant="filled" fullWidth error={!!errors.status}>
          <InputLabel id="status-label">{msg({ id: 'field.status.label' })}</InputLabel>
          <Controller
            name="status"
            control={control}
            rules={{ required: msg({ id: 'field.status.required' }) }}
            render={({ field }) => (
              <Select required {...field} label={msg({ id: 'field.status.label' })} size="small">
                <MenuItem value="ACTIVATED">{msg({ id: 'status.activated' })}</MenuItem>
                <MenuItem value="EXPIRED">{msg({ id: 'status.expired' })}</MenuItem>
              </Select>
            )}
          />
          {errors.status && <FormHelperText>{errors.status.message}</FormHelperText>}
        </FormControl>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.description.label' })}
              variant="filled"
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('description')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <FormControl fullWidth sx={{ mt: '16px !important' }}>
          <FormLabel component="legend" id="roles-label" sx={{ fontSize: 'medium' }}>
            {msg({ id: 'field.roles.label' })}
          </FormLabel>
          <Controller
            name="roles"
            control={control}
            render={({ field }) => (
              <DataGrid
                {...field}
                rows={roles}
                columns={[
                  { field: 'id', headerName: 'ID', width: 50, resizable: false },
                  { field: 'code', headerName: 'Code', width: 200, resizable: false },
                  { field: 'label', headerName: 'Label', width: 300, resizable: false }
                ]}
                initialState={{
                  density: 'compact'
                }}
                checkboxSelection
                rowHeight={38}
                loading={roleLoading}
                onSelectionModelChange={handleRoleSelectionChange}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={handleRowSelectionModelChange}
                disableColumnResize
                hideFooter
              />
            )}
          />
        </FormControl>
      </Stack>
      <Divider sx={{ mt: 0 }} />
      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton type="submit" fullWidth color="primary">
          {isCreating ? msg({ id: 'button.create' }) : msg({ id: 'button.update' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

ApiKeyDetails.propTypes = {
  apiKey: PropTypes.shape({
    id: PropTypes.number,
    displayableKey: PropTypes.string,
    owner: PropTypes.string,
    validityPeriod: PropTypes.number,
    status: PropTypes.string,
    description: PropTypes.string,
    roles: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        code: PropTypes.string,
        label: PropTypes.string,
        description: PropTypes.string
      })
    )
  }),
  onSubmit: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired
};

export default ApiKeyDetails;
